import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import EmailIcon from '@mui/icons-material/Email';

import { StatValue } from './StatValue';

import * as adminApi from '../api/admin';
import type { EmailDeliverySummary } from '../api/admin';

/**
 * Platform-dashboard email-delivery card (EMAIL-DELIVERY).
 *
 * Headline = emails sent (accepted by the provider) over the last 30
 * days. Subtext breaks out delivered / bounced / failed and the bounce
 * rate — the number that flags a deliverability problem (a spike of
 * institutional bounces, a mis-set domain), all on one baseline with the
 * headline number.
 *
 * There WAS a sparkline here overlaying daily sent and bounced. It was
 * removed 2026-09-19: this card sits in a stretch-aligned Grid with seven
 * others, so it set the height of the entire dashboard summary row, and a
 * 30-day trend line was not worth the vertical space it cost every card
 * beside it. The per-day series is still returned by the API if it is ever
 * wanted somewhere with room for it.
 *
 * Powered by public.email_log: every send logs a row; the provider
 * delivery webhook flips status to delivered/bounced. Polls every 60 s.
 */

const POLL_INTERVAL_MS = 60_000;

export function EmailDeliveryCard({
  size = { xs: 12, md: 6 },
  tenantId = null,
}: {
  size?: React.ComponentProps<typeof Grid>['size'];
  /** When set, the card is scoped to one tenant's outbound mail (tenant-admin
   *  dashboard). Omitted → the platform-wide superadmin roll-up. */
  tenantId?: string | null;
} = {}) {
  const [summary, setSummary] = useState<EmailDeliverySummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = tenantId
          ? await adminApi.getTenantEmailDelivery(tenantId, 30)
          : await adminApi.getEmailDelivery(30);
        if (!cancelled) {
          setSummary(next);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            (err as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? 'failed to load email delivery',
          );
        }
      }
    };
    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [tenantId]);

  return (
    <Grid size={size} sx={{ display: 'flex' }}>
      <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* This card sets the height of the whole eight-card row (the Grid
            stretches every sibling to the tallest), so its padding and
            sparkline are what actually decide how much vertical space the
            dashboard summary occupies — not the stat tiles. */}
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            px: 1.5,
            py: 1.25,
            '&:last-child': { pb: 1.25 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <EmailIcon color="primary" fontSize="small" />
            <Typography variant="overline" color="text.secondary">
              Email delivery (30 days)
            </Typography>
          </Stack>
          {summary === null && error === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : summary !== null ? (
            <DeliveryBody summary={summary} />
          ) : null}
        </CardContent>
      </Card>
    </Grid>
  );
}

function DeliveryBody({ summary }: { summary: EmailDeliverySummary }) {
  const bounceRate =
    summary.sent > 0 ? (summary.bounced / summary.sent) * 100 : 0;
  const worst = summary.top_bounced_domains[0];
  return (
    <>
      {/* Number and breakdown on one baseline — the same two-row shape as
          the stat tiles beside it. The daily sparkline that used to live
          here was dropped: this card sets the height of the whole
          eight-card row (the Grid stretches siblings to the tallest), and
          a 30-day trend line was not worth the vertical space it cost
          every other card on the dashboard. */}
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0 }}>
        <StatValue value={summary.sent.toLocaleString()} size={30} />
      <Typography variant="caption" color="text.secondary">
        sent ·{' '}
        <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
          {summary.delivered.toLocaleString()} delivered
        </Box>{' '}
        ·{' '}
        <Tooltip
          title={
            worst
              ? `Worst: ${worst.domain} (${worst.bounced} of ${worst.sent})`
              : 'No bounces'
          }
        >
          <Box
            component="span"
            sx={{
              color: summary.bounced > 0 ? 'error.main' : 'text.secondary',
              fontWeight: 700,
            }}
          >
            {summary.bounced.toLocaleString()} bounced ({bounceRate.toFixed(1)}%)
          </Box>
        </Tooltip>
        {summary.failed > 0 ? ` · ${summary.failed} failed` : ''}
      </Typography>
      </Stack>
    </>
  );
}
